import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail, Bird, Sun, Moon, Scale } from 'lucide-react';

interface TermsOfServiceProps {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  language: 'pt' | 'en';
  setLanguage: (lang: 'pt' | 'en') => void;
}

export const TermsOfService: React.FC<TermsOfServiceProps> = ({ theme, toggleTheme, language, setLanguage }) => {

  const content = {
    pt: {
      pageTitle: "Termos de Serviço",
      back: "Voltar",
      subtitle: "Athena Studies • Parthenon Planner",
      intro: (<>Estes Termos de Serviço regulam o uso do aplicativo <strong>Parthenon Planner</strong>, desenvolvido e mantido pela <strong>Athena Studies</strong>. Ao acessar ou utilizar o aplicativo, você declara que leu, compreendeu e concorda com estes Termos.</>),
      introNote: "Caso não concorde com qualquer disposição aqui descrita, recomendamos que não utilize o serviço.",
      privacyNotice: (<>O tratamento dos seus dados pessoais é descrito em nossa <strong>Política de Privacidade</strong>, que complementa estes Termos e deve ser lida em conjunto com eles.</>),
      privacyLink: "/privacidade",
      sections: [
        {
          title: "1. Sobre o Parthenon Planner",
          paragraphs: [
            <>O <strong>Parthenon Planner</strong> é um aplicativo de organização e gestão de estudos, voltado a estudantes que desejam estruturar sua rotina acadêmica, acompanhar seu progresso e integrar estudos com professores, tutores ou colegas.</>,
            "A Athena Studies reserva-se o direito de modificar, suspender ou atualizar funcionalidades do aplicativo visando melhorias contínuas da experiência do usuário."
          ]
        },
        {
          title: "2. Elegibilidade",
          bullets: ["Idade mínima de 13 anos", "Dados verídicos no cadastro", "Responsabilidade por credenciais", "Menores devem ter supervisão"],
          note: "Menores de 18 anos devem utilizar o aplicativo com o conhecimento e acompanhamento de um responsável legal."
        },
        {
          title: "3. Cadastro e conta",
          paragraph: "O acesso ao Parthenon Planner depende da criação de uma conta individual e intransferível.",
          listItems: [
            "O usuário é responsável por todas as atividades realizadas em sua conta",
            "A segurança do login e da senha é de responsabilidade do usuário",
            "A Athena Studies não se responsabiliza por acessos não autorizados decorrentes de negligência do próprio usuário"
          ]
        },
        {
          title: "4. Uso adequado do serviço",
          paragraph: "Ao utilizar o Parthenon Planner, o usuário compromete-se a:",
          scaleBullets: [
            "Fins educacionais e organizacionais apenas",
            "Não compartilhar conteúdos ofensivos, ilegais ou que violem direitos de terceiros",
            "Não tentar burlar sistemas de segurança ou acessar dados de outros usuários sem autorização",
            "Não usar para fins comerciais não autorizados pela Athena Studies"
          ],
          warning: "O descumprimento destas regras pode resultar em suspensão ou encerramento da conta, sem prejuízo das sanções legais aplicáveis."
        },
        {
          title: "5. Conteúdo do usuário",
          paragraphs: [
            "Todo conteúdo inserido no Parthenon Planner permanece de propriedade exclusiva do próprio usuário.",
            "Ao utilizar o aplicativo, o usuário concede à Athena Studies uma licença limitada, não exclusiva, não sublicenciável e revogável para armazenar, processar e exibir o conteúdo exclusivamente na medida necessária para a prestação do serviço. Essa licença se encerra automaticamente quando o conteúdo é excluído ou quando a conta é encerrada.",
            "A Athena Studies não utilizará o conteúdo inserido pelo usuário para fins de publicidade, treinamento de modelos de inteligência artificial ou quaisquer outros fins além dos descritos nestes Termos."
          ]
        },
        {
          title: "6. Comunicações",
          paragraphs: [
            "Ao criar uma conta, o usuário concorda em receber comunicações operacionais relacionadas ao serviço, como confirmações de cadastro, notificações de segurança e avisos de alterações nestes Termos.",
            "Comunicações de natureza informativa ou educacional serão enviadas apenas com o consentimento expresso do usuário, que poderá revogar essa preferência a qualquer momento."
          ]
        },
        {
          title: "7. Integração entre usuários",
          paragraphs: [
            "O Parthenon Planner permite a integração entre estudantes, professores e colegas para compartilhamento de tarefas, materiais e compromissos.",
            "Esse compartilhamento ocorre exclusivamente por iniciativa do usuário. A Athena Studies não se responsabiliza por eventuais usos indevidos decorrentes dessa integração."
          ]
        },
        {
          title: "8. Gratuidade do Serviço",
          paragraphs: [
            "O Parthenon Planner é disponibilizado de forma totalmente gratuita e ilimitada para todos os usuários.",
            "Não há cobranças, assinaturas ou taxas para o uso completo das funcionalidades de organização e planejamento do aplicativo.",
            "A Athena Studies se reserva o direito de, futuramente, introduzir planos opcionais pagos com funcionalidades adicionais, mediante comunicação prévia aos usuários. A introdução de tais planos não afetará o acesso gratuito às funcionalidades atualmente disponíveis."
          ]
        },
        {
          title: "9. Cancelamento e encerramento da conta",
          paragraphs: [
            "O usuário pode solicitar o cancelamento da conta a qualquer momento pelo canal de contato indicado nestes Termos.",
            "A Athena Studies poderá suspender ou encerrar contas que violem estes Termos, especialmente em casos de uso indevido, fraude ou violação de leis, com ou sem aviso prévio a depender da gravidade da infração."
          ]
        },
        {
          title: "10. Limitação de responsabilidade",
          paragraphs: [
            <em>O Parthenon Planner é fornecido "como está", sem garantias de funcionamento ininterrupto ou livre de erros.</em>,
            "A Athena Studies não se responsabiliza por perdas decorrentes de falhas técnicas, interrupções do serviço ou decisões acadêmicas, profissionais ou pessoais tomadas pelo usuário com base em informações organizadas no aplicativo."
          ]
        },
        {
          title: "11. Propriedade intelectual",
          paragraphs: [
            "Todo o conteúdo estrutural do Parthenon Planner, incluindo design, layout, textos e marca, é de propriedade exclusiva da Athena Studies.",
            "É proibida a reprodução, distribuição ou uso não autorizado desses elementos sem consentimento expresso e por escrito da Athena Studies."
          ]
        },
        {
          title: "12. Alterações nos termos",
          paragraphs: ["Estes Termos podem ser atualizados periodicamente. Alterações relevantes serão comunicadas ao usuário por e-mail ou mediante aviso no aplicativo com antecedência razoável. O uso continuado do serviço após o prazo comunicado implica aceitação dos novos Termos."]
        },
        {
          title: "13. Lei aplicável e foro",
          paragraphs: ["Estes Termos são regidos pelas leis da República Federativa do Brasil, incluindo o Código de Defesa do Consumidor (Lei nº 8.078/1990) e a LGPD (Lei nº 13.709/2018). Fica eleito o foro do domicílio do usuário para dirimir eventuais controvérsias."]
        },
        {
          title: "14. Contato",
          contactLabel: "Suporte e Atendimento"
        }
      ],
      lastUpdated: "Última atualização: Janeiro de 2026",
      footer: "© 2026 • DOMINE O SEU FUTURO"
    },
    en: {
      pageTitle: "Terms of Service",
      back: "Back",
      subtitle: "Athena Studies • Parthenon Planner",
      intro: (<>These Terms of Service govern the use of the <strong>Parthenon Planner</strong> application, developed and maintained by <strong>Athena Studies</strong>. By accessing or using the application, you declare that you have read, understood, and agree to these Terms.</>),
      introNote: "If you do not agree with any provision described here, we recommend that you do not use the service.",
      privacyNotice: (<>The processing of your personal data is described in our <strong>Privacy Policy</strong>, which complements these Terms and should be read together with them.</>),
      privacyLink: "/privacidade",
      sections: [
        {
          title: "1. About Parthenon Planner",
          paragraphs: [
            <>The <strong>Parthenon Planner</strong> is a study organization and management application aimed at students who wish to structure their academic routine, track their progress, and integrate their studies with teachers, tutors, or peers.</>,
            "Athena Studies reserves the right to modify, suspend, or update app features in pursuit of continuous improvements to the user experience."
          ]
        },
        {
          title: "2. Eligibility",
          bullets: ["Minimum age of 13 years", "Accurate data at registration", "Responsibility for credentials", "Minors must have supervision"],
          note: "Users under 18 must use the application with the knowledge and accompaniment of a legal guardian."
        },
        {
          title: "3. Registration and account",
          paragraph: "Access to Parthenon Planner requires creating an individual, non-transferable account.",
          listItems: [
            "The user is responsible for all activities carried out under their account",
            "The security of the login and password is the user's responsibility",
            "Athena Studies is not responsible for unauthorized access resulting from the user's own negligence"
          ]
        },
        {
          title: "4. Appropriate use of the service",
          paragraph: "By using Parthenon Planner, the user commits to:",
          scaleBullets: [
            "Educational and organizational purposes only",
            "Not sharing offensive, illegal, or third-party rights-infringing content",
            "Not attempting to circumvent security systems or access other users' data without authorization",
            "Not using the app for commercial purposes not authorized by Athena Studies"
          ],
          warning: "Failure to comply with these rules may result in account suspension or termination, without prejudice to applicable legal sanctions."
        },
        {
          title: "5. User content",
          paragraphs: [
            "All content entered into Parthenon Planner remains the exclusive property of the user.",
            "By using the app, the user grants Athena Studies a limited, non-exclusive, non-sublicensable, and revocable license to store, process, and display the content only to the extent necessary for providing the service. This license terminates automatically when the content is deleted or when the account is closed.",
            "Athena Studies will not use user-submitted content for advertising, training artificial intelligence models, or any purposes beyond those described in these Terms."
          ]
        },
        {
          title: "6. Communications",
          paragraphs: [
            "By creating an account, the user agrees to receive operational communications related to the service, such as registration confirmations, security notifications, and notices of changes to these Terms.",
            "Informational or educational communications will only be sent with the user's express consent, which may be revoked at any time."
          ]
        },
        {
          title: "7. Integration between users",
          paragraphs: [
            "Parthenon Planner allows integration between students, teachers, and peers for sharing tasks, materials, and commitments.",
            "This sharing occurs exclusively at the user's initiative. Athena Studies is not responsible for any misuse arising from this integration."
          ]
        },
        {
          title: "8. Free Service",
          paragraphs: [
            "Parthenon Planner is provided entirely free of charge and without limits for all users.",
            "There are no charges, subscriptions, or fees for full access to the app's organization and planning features.",
            "Athena Studies reserves the right to introduce optional paid plans with additional features in the future, with prior notice to users. The introduction of such plans will not affect free access to currently available features."
          ]
        },
        {
          title: "9. Account cancellation and termination",
          paragraphs: [
            "The user may request account cancellation at any time through the contact channel provided in these Terms.",
            "Athena Studies may suspend or terminate accounts that violate these Terms, especially in cases of misuse, fraud, or violation of laws, with or without prior notice depending on the severity of the breach."
          ]
        },
        {
          title: "10. Limitation of liability",
          paragraphs: [
            <em>Parthenon Planner is provided "as is", without guarantees of uninterrupted or error-free operation.</em>,
            "Athena Studies is not liable for losses resulting from technical failures, service interruptions, or academic, professional, or personal decisions made by the user based on information organized in the application."
          ]
        },
        {
          title: "11. Intellectual property",
          paragraphs: [
            "All structural content of Parthenon Planner, including design, layout, texts, and brand, is the exclusive property of Athena Studies.",
            "Reproduction, distribution, or unauthorized use of these elements without the express written consent of Athena Studies is prohibited."
          ]
        },
        {
          title: "12. Changes to the terms",
          paragraphs: ["These Terms may be updated periodically. Material changes will be communicated to users by email or via an in-app notice with reasonable advance notice. Continued use of the service after the communicated deadline implies acceptance of the new Terms."]
        },
        {
          title: "13. Applicable law and jurisdiction",
          paragraphs: ["These Terms are governed by the laws of the Federative Republic of Brazil, including the Consumer Protection Code (Law No. 8.078/1990) and the LGPD (Law No. 13.709/2018). The user's domicile is elected as the venue to resolve any disputes."]
        },
        {
          title: "14. Contact",
          contactLabel: "Support and Customer Service"
        }
      ],
      lastUpdated: "Last updated: January 2026",
      footer: "© 2026 • MASTER YOUR FUTURE"
    }
  };

  const t = content[language];
  const sections = t.sections;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-sans selection:bg-athena-coral selection:text-white transition-colors duration-300">

      {/* Header Minimalista */}
      <header className="fixed top-0 w-full z-[100] backdrop-blur-xl bg-white/70 dark:bg-slate-900/70 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="p-2 bg-athena-coral rounded-xl text-white shadow-lg group-hover:scale-110 transition-transform">
              <Bird size={24} />
            </div>
            <h1 className="text-xl font-black tracking-tighter text-athena-teal dark:text-white uppercase">
              Parthenon<span className="text-athena-coral">.</span>
            </h1>
          </Link>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setLanguage('pt')}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  language === 'pt'
                    ? 'bg-slate-100 dark:bg-slate-800 ring-2 ring-athena-blue dark:ring-blue-500 scale-105 shadow-sm'
                    : 'opacity-40 hover:opacity-100 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
                title="Português"
              >
                <img src="https://flagcdn.com/br.svg" alt="Português" className="w-5 h-5 rounded-full object-cover" />
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  language === 'en'
                    ? 'bg-slate-100 dark:bg-slate-800 ring-2 ring-athena-blue dark:ring-blue-500 scale-105 shadow-sm'
                    : 'opacity-40 hover:opacity-100 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
                title="English"
              >
                <img src="https://flagcdn.com/us.svg" alt="English" className="w-5 h-5 rounded-full object-cover" />
              </button>
            </div>

            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 shadow-sm hover:scale-110 active:scale-95 transition-all"
              title={language === 'pt' ? "Trocar Tema" : "Toggle Theme"}
            >
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <Link
              to="/"
              className="px-6 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-950 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:opacity-90 active:scale-95 transition-all flex items-center gap-2"
            >
              <ArrowLeft size={14} /> {t.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto bg-white dark:bg-slate-900 rounded-[2.5rem] border-2 border-slate-200 dark:border-slate-800 p-8 md:p-16 shadow-2xl">

          <div className="flex items-center gap-4 mb-10 text-athena-coral">
            <FileText size={48} />
            <div>
              <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-slate-950 dark:text-white leading-none">{t.pageTitle}</h1>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mt-2">{t.subtitle}</p>
            </div>
          </div>

          <div className="prose prose-slate dark:prose-invert max-w-none space-y-8 text-slate-700 dark:text-slate-300">
            <p className="text-lg font-bold leading-relaxed italic">{t.intro}</p>
            <p className="font-medium">{t.introNote}</p>

            {/* Aviso de link para Política de Privacidade */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
              <p className="text-xs font-bold text-slate-600 dark:text-slate-400">
                {t.privacyNotice}{' '}
                <Link to={t.privacyLink} className="text-athena-teal dark:text-athena-coral underline underline-offset-2">
                  {language === 'pt' ? 'Ler Política de Privacidade →' : 'Read Privacy Policy →'}
                </Link>
              </p>
            </div>

            <div className="h-px bg-slate-100 dark:bg-slate-800 w-full" />

            {sections.map((section, idx) => {
              const s = section as any;

              // Seção de Contato
              if (s.contactLabel) {
                return (
                  <section key={idx} className="space-y-4">
                    <h2 className="text-xl font-black text-athena-teal dark:text-athena-coral uppercase tracking-tight">{s.title}</h2>
                    <div className="p-6 md:p-8 bg-athena-coral/5 dark:bg-athena-coral/10 rounded-3xl border-2 border-athena-coral/20 flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 text-center sm:text-left">
                      <div className="w-12 h-12 bg-athena-coral rounded-2xl flex items-center justify-center text-white shadow-lg shrink-0">
                        <Mail size={24} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-1">{s.contactLabel}</p>
                        <p className="text-base md:text-lg font-black text-athena-coral break-all">studiesathena2025@gmail.com</p>
                      </div>
                    </div>
                  </section>
                );
              }

              // Seção genérica
              return (
                <section key={idx} className="space-y-4">
                  <h2 className="text-xl font-black text-athena-teal dark:text-athena-coral uppercase tracking-tight">{s.title}</h2>
                  {s.paragraph && <p className="font-medium">{s.paragraph}</p>}
                  {s.paragraphs && s.paragraphs.map((p: any, pi: number) => (
                    <p key={pi} className="font-medium">{p}</p>
                  ))}
                  {s.bullets && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {s.bullets.map((item: string, i: number) => (
                          <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/30 rounded-2xl border border-slate-100 dark:border-slate-800">
                            <div className="w-2 h-2 rounded-full bg-athena-coral" />
                            <span className="text-xs font-black uppercase">{item}</span>
                          </div>
                        ))}
                      </div>
                      {s.note && <p className="text-sm font-bold text-slate-500 mt-2">{s.note}</p>}
                    </>
                  )}
                  {s.listItems && (
                    <ul className="list-disc pl-5 space-y-2 text-sm font-bold">
                      {s.listItems.map((item: string, i: number) => <li key={i}>{item}</li>)}
                    </ul>
                  )}
                  {s.scaleBullets && (
                    <>
                      <ul className="space-y-3">
                        {s.scaleBullets.map((item: string, i: number) => (
                          <li key={i} className="flex items-start gap-3 text-sm font-bold">
                            <Scale size={18} className="text-athena-teal shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                      {s.warning && (
                        <div className="p-4 bg-rose-50 dark:bg-rose-950/20 rounded-xl border border-rose-200 dark:border-rose-900">
                          <p className="text-xs font-bold text-rose-800 dark:text-rose-400">{s.warning}</p>
                        </div>
                      )}
                    </>
                  )}
                </section>
              );
            })}

            <div className="h-px bg-slate-100 dark:bg-slate-800 w-full" />
            <p className="text-center text-[10px] font-black uppercase text-slate-400 tracking-[0.3em]">{t.lastUpdated}</p>
          </div>
        </div>
      </main>

      {/* Footer Minimalista */}
      <footer className="py-10 text-center border-t border-slate-200 dark:border-slate-800">
        <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{t.footer}</p>
      </footer>
    </div>
  );
};